/**
 * Iter 194 — Change Control, Internal Audit, Risk Management, Waste, Recall summary tabs.
 * All modules follow the same architectural pattern as GMPQMS.jsx (Iter 193).
 *
 * IMPORTANT: Change Control approval does NOT auto-modify source records (Formula/SOP/
 * Equipment). Users must click "İlgili Kayda Git" to navigate to the source manually.
 * Waste records do NOT mutate stock.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, Btn, Badge, Input, Select, Textarea, Modal } from "@/components/UI";
import GmpEvidencePanel from "@/components/GmpEvidencePanel";
import { Plus, ExternalLink, Shield, AlertOctagon, ClipboardCheck, Recycle, Package } from "lucide-react";
import { todayTR } from "@/lib/date";

const today = () => todayTR();
const fmtDate = (iso) => (iso || "").slice(0, 10);
const fmtDT = (iso) => (iso || "").slice(0, 19).replace("T", " ");

const STATUS_LEVEL = {
  draft: "inactive", under_review: "attention", approved: "critical",
  implementation: "critical", effectiveness_check: "attention",
  closed: "ok", rejected: "missing", cancelled: "inactive",
  planned: "inactive", in_progress: "attention", completed: "critical",
  open: "attention", mitigating: "critical", monitoring: "attention",
};
const CLASS_LEVEL = { observation: "inactive", minor: "attention", major: "critical", critical: "missing" };
const RPN_LEVEL = { low: "ok", medium: "attention", high: "critical", critical: "missing" };


/* ============= 1) CHANGE CONTROL ============= */
const CC_TYPES = [
  { k: "process", l: "Süreç" }, { k: "formula", l: "Formül" }, { k: "sop", l: "SOP" },
  { k: "equipment", l: "Ekipman" }, { k: "supplier", l: "Tedarikçi" }, { k: "material", l: "Hammadde" },
  { k: "packaging", l: "Ambalaj" }, { k: "facility", l: "Tesis" }, { k: "qc_method", l: "QC Yöntemi" },
  { k: "other", l: "Diğer" },
];
const SEVERITIES = [
  { k: "low", l: "Düşük" }, { k: "medium", l: "Orta" }, { k: "high", l: "Yüksek" }, { k: "critical", l: "Kritik" },
];

export function ChangeControlTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: "", change_type: "", severity: "", q: "" });
  const [modal, setModal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [transModal, setTransModal] = useState(null); // {doc, action, label}
  const [refs, setRefs] = useState({ formulas: [], materials: [], suppliers: [], equipment: [], sops: [] });

  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setRows((await api.get("/gmp/change-controls", params)) || []);
  };
  useEffect(() => { load(); }, [f.status, f.change_type, f.severity]);
  useEffect(() => {
    Promise.all([
      api.get("/formulas").catch(() => []),
      api.get("/materials").catch(() => []),
      api.get("/suppliers").catch(() => []),
      api.get("/gmp/equipment").catch(() => []),
      api.get("/gmp/documents").catch(() => []),
    ]).then(([f, m, s, e, d]) => setRefs({ formulas: f || [], materials: m || [], suppliers: s || [], equipment: e || [], sops: d || [] }));
  }, []);

  const openCreate = () => setModal({ mode: "create", doc: {
    title: "", description: "", change_type: "process", severity: "medium",
    requested_at: today(), justification: "", risk_assessment: ""
  } });
  const save = async () => {
    if (!modal.doc.title?.trim() || !modal.doc.description?.trim()) return alert("Başlık ve açıklama zorunlu");
    try {
      if (modal.mode === "create") await api.post("/gmp/change-controls", modal.doc);
      else await api.put(`/gmp/change-controls/${modal.doc.id}`, modal.doc);
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const doTransition = async () => {
    const { doc, action, notes, reject_reason, effectiveness_result } = transModal;
    try {
      await api.post(`/gmp/change-controls/${doc.id}/${action}`, { notes, reject_reason, effectiveness_result });
      setTransModal(null); load(); setDetail(null);
    } catch (e) { alert(e.response?.data?.detail || "İşlem başarısız"); }
  };
  const openCapa = async (r) => {
    const t = prompt("CAPA başlığı", `CAPA — ${r.number}: ${r.title}`);
    if (!t) return;
    try { await api.post(`/gmp/change-controls/${r.id}/capa`, { title: t, priority: "high" }); alert("CAPA açıldı. GMP → CAPA sekmesinden takip edin."); load(); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const del = async (r) => { if (window.confirm(`${r.number} arşivlensin mi?`)) { try { await api.del(`/gmp/change-controls/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };

  // Deeplink helper for "İlgili Kayda Git"
  const gotoLink = (doc) => {
    if (doc.formula_id) return `/formulas`;
    if (doc.material_id || doc.raw_material_card_id) return `/inventory`;
    if (doc.supplier_id) return `/tedarikciler`;
    if (doc.equipment_id) return `/gmp`;
    if (doc.production_order_id) return `/uretim`;
    if (doc.qc_test_id) return `/quality`;
    if (doc.sop_document_id) return `/gmp`;
    return null;
  };

  const nextActions = (r) => {
    const s = r.status;
    const acts = [];
    if (s === "draft") acts.push(["submit", "Onaya Gönder", "primary"]);
    if (s === "under_review") { acts.push(["approve", "Onayla", "primary"]); acts.push(["reject", "Reddet", "danger"]); }
    if (s === "approved") acts.push(["start-implementation", "Uygulamaya Başla", "primary"]);
    if (s === "implementation") { acts.push(["effectiveness", "Etkinlik Kontrolü", "secondary"]); acts.push(["close", "Kapat", "primary"]); }
    if (s === "effectiveness_check") acts.push(["close", "Kapat", "primary"]);
    if (!["closed", "rejected", "cancelled"].includes(s)) acts.push(["cancel", "İptal", "danger"]);
    return acts;
  };

  return (
    <div>
      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border rounded-lg p-3">
        <div className="min-w-[200px] flex-1"><Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} data-testid="cc-search"/></div>
        <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="cc-filter-status">
          <option value="">Tümü</option>
          <option value="draft">Taslak</option><option value="under_review">İncelemede</option>
          <option value="approved">Onaylı</option><option value="implementation">Uygulanıyor</option>
          <option value="effectiveness_check">Etkinlik Kontrolü</option>
          <option value="closed">Kapalı</option><option value="rejected">Reddedildi</option><option value="cancelled">İptal</option>
        </Select>
        <Select label="Tip" value={f.change_type} onChange={e => setF({ ...f, change_type: e.target.value })} data-testid="cc-filter-type">
          <option value="">Tümü</option>{CC_TYPES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
        </Select>
        <Select label="Kritiklik" value={f.severity} onChange={e => setF({ ...f, severity: e.target.value })} data-testid="cc-filter-sev">
          <option value="">Tümü</option>{SEVERITIES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
        </Select>
        <div className="flex-1"/>
        {isAdmin && <Btn onClick={openCreate} data-testid="cc-new"><Plus size={14}/> Yeni Değişiklik</Btn>}
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]" data-testid="cc-table">
          <thead className="bg-slate-100 text-slate-700 text-xs"><tr>
            <th className="text-left px-3 py-2">No</th><th className="text-left px-3 py-2">Başlık</th>
            <th className="text-left px-3 py-2">Tip</th><th className="text-left px-3 py-2">Kritiklik</th>
            <th className="text-left px-3 py-2">Durum</th><th className="text-left px-3 py-2">Tarih</th>
            <th className="text-left px-3 py-2 w-64">İşlem</th>
          </tr></thead>
          <tbody>
            {rows.filter(r => !f.q || (r.number + r.title).toLowerCase().includes(f.q.toLowerCase())).map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`cc-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                <td className="px-3 py-2 font-semibold">{r.title}</td>
                <td className="px-3 py-2 text-xs">{CC_TYPES.find(x => x.k === r.change_type)?.l || r.change_type}</td>
                <td className="px-3 py-2"><Badge level={r.severity === "critical" ? "missing" : r.severity === "high" ? "critical" : "attention"}>{r.severity}</Badge></td>
                <td className="px-3 py-2"><Badge level={STATUS_LEVEL[r.status]}>{r.status}</Badge></td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.created_at)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <Btn size="sm" variant="ghost" onClick={() => setDetail(r)} data-testid={`cc-view-${r.id}`}>Detay</Btn>
                    {!["closed", "rejected", "cancelled"].includes(r.status) && <Btn size="sm" variant="secondary" onClick={() => openCapa(r)} data-testid={`cc-capa-${r.id}`}><Shield size={11}/> CAPA</Btn>}
                    <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`cc-delete-${r.id}`}>Sil</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Değişiklik kontrolü yok.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* CREATE */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.mode === "create" ? "Yeni Değişiklik Kontrolü" : "Düzenle"} size="lg">
        {modal && (<div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Başlık *" value={modal.doc.title} onChange={e => setModal({ ...modal, doc: { ...modal.doc, title: e.target.value } })} data-testid="cc-form-title"/></div>
          <div className="col-span-2"><Textarea label="Açıklama *" rows={3} value={modal.doc.description} onChange={e => setModal({ ...modal, doc: { ...modal.doc, description: e.target.value } })} data-testid="cc-form-desc"/></div>
          <Select label="Tip" value={modal.doc.change_type} onChange={e => setModal({ ...modal, doc: { ...modal.doc, change_type: e.target.value } })} data-testid="cc-form-type">
            {CC_TYPES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
          </Select>
          <Select label="Kritiklik" value={modal.doc.severity} onChange={e => setModal({ ...modal, doc: { ...modal.doc, severity: e.target.value } })} data-testid="cc-form-severity">
            {SEVERITIES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
          </Select>
          <div className="col-span-2"><Textarea label="Gerekçe" rows={2} value={modal.doc.justification || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, justification: e.target.value } })} data-testid="cc-form-just"/></div>
          <div className="col-span-2"><Textarea label="Risk Değerlendirmesi" rows={2} value={modal.doc.risk_assessment || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, risk_assessment: e.target.value } })} data-testid="cc-form-risk"/></div>
          <div className="col-span-2 border-t pt-2 mt-1 text-xs font-semibold text-slate-500 uppercase">Bağlı Kayıtlar (opsiyonel, otomatik değiştirilmez)</div>
          <Select label="Formül" value={modal.doc.formula_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, formula_id: e.target.value } })} data-testid="cc-form-formula">
            <option value="">— Seç —</option>
            {refs.formulas.slice(0, 500).map(f => <option key={f.id} value={f.id}>{f.code} — {f.product_name} {f.revision ? `(${f.revision})` : ""}</option>)}
          </Select>
          <Select label="Hammadde" value={modal.doc.material_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, material_id: e.target.value } })} data-testid="cc-form-material">
            <option value="">— Seç —</option>
            {refs.materials.slice(0, 500).map(m => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
          </Select>
          <Select label="Tedarikçi" value={modal.doc.supplier_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, supplier_id: e.target.value } })} data-testid="cc-form-supplier">
            <option value="">— Seç —</option>
            {refs.suppliers.slice(0, 500).map(s => <option key={s.id} value={s.id}>{s.legal_name || s.name}</option>)}
          </Select>
          <Select label="Ekipman" value={modal.doc.equipment_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, equipment_id: e.target.value } })} data-testid="cc-form-equipment">
            <option value="">— Seç —</option>
            {refs.equipment.slice(0, 500).map(e => <option key={e.id} value={e.id}>{e.code} — {e.name}</option>)}
          </Select>
          <Select label="SOP / Doküman" value={modal.doc.sop_document_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, sop_document_id: e.target.value } })} data-testid="cc-form-sop">
            <option value="">— Seç —</option>
            {refs.sops.slice(0, 500).map(d => <option key={d.id} value={d.id}>{d.doc_no || d.code} — {d.title}</option>)}
          </Select>
          <Input label="Tesis / Alan" value={modal.doc.facility_area || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, facility_area: e.target.value } })} data-testid="cc-form-facility"/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn><Btn onClick={save} data-testid="cc-form-save">Kaydet</Btn></div>
      </Modal>

      {/* DETAIL */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={`Değişiklik ${detail?.number || ""}`} size="lg">
        {detail && (<div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><b>Durum:</b> <Badge level={STATUS_LEVEL[detail.status]}>{detail.status}</Badge></div>
            <div><b>Kritiklik:</b> {detail.severity}</div>
            <div><b>Tip:</b> {CC_TYPES.find(x => x.k === detail.change_type)?.l}</div>
            <div><b>Tarih:</b> {fmtDate(detail.created_at)}</div>
          </div>
          <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Açıklama</div><div className="bg-slate-50 border rounded p-2 whitespace-pre-wrap">{detail.description}</div></div>
          {detail.justification && <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Gerekçe</div><div className="bg-slate-50 border rounded p-2 whitespace-pre-wrap">{detail.justification}</div></div>}
          {detail.risk_assessment && <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Risk Değerlendirmesi</div><div className="bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap">{detail.risk_assessment}</div></div>}
          {/* Snapshot chips + goto */}
          <div className="grid grid-cols-2 gap-2 text-[11px]">
            {detail.formula_snapshot && <SnapshotChip label="Formül" value={`${detail.formula_snapshot.code} — ${detail.formula_snapshot.product_name}`}/>}
            {detail.material_snapshot && <SnapshotChip label="Hammadde" value={`${detail.material_snapshot.code} — ${detail.material_snapshot.name}`}/>}
            {detail.supplier_snapshot && <SnapshotChip label="Tedarikçi" value={detail.supplier_snapshot.name}/>}
            {detail.equipment_snapshot && <SnapshotChip label="Ekipman" value={`${detail.equipment_snapshot.code} — ${detail.equipment_snapshot.name}`}/>}
            {detail.sop_snapshot && <SnapshotChip label="SOP" value={`${detail.sop_snapshot.doc_no} — ${detail.sop_snapshot.title}`}/>}
          </div>
          {gotoLink(detail) && (
            <div className="bg-sky-50 border border-sky-200 rounded p-2 text-xs flex items-center justify-between">
              <span>ℹ Kaynak kayıt Change Control tarafından <b>otomatik değiştirilmez</b>. Manuel güncellemek için:</span>
              <a href={gotoLink(detail)} className="text-sky-700 font-semibold flex items-center gap-1 hover:underline" data-testid="cc-goto-source">
                İlgili Kayda Git <ExternalLink size={12}/>
              </a>
            </div>
          )}
          <div className="border-t pt-2">
            <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Workflow İşlemleri</div>
            <div className="flex flex-wrap gap-2">
              {nextActions(detail).map(([action, label, variant]) => (
                <Btn key={action} size="sm" variant={variant} onClick={() => setTransModal({ doc: detail, action, label })} data-testid={`cc-action-${action}-${detail.id}`}>{label}</Btn>
              ))}
            </div>
          </div>
          {(detail.capa_request_ids || []).length > 0 && <div className="bg-sky-50 border border-sky-200 rounded p-2 text-xs"><b>🛡 Bağlı CAPA:</b> {detail.capa_request_ids.length} adet</div>}
        </div>)}
      </Modal>

      {/* TRANSITION MODAL */}
      <Modal open={!!transModal} onClose={() => setTransModal(null)} title={transModal?.label || ""} size="sm">
        {transModal && (<div className="space-y-3">
          <Textarea label="Notlar" rows={3} value={transModal.notes || ""} onChange={e => setTransModal({ ...transModal, notes: e.target.value })} data-testid="cc-trans-notes"/>
          {transModal.action === "reject" && <Textarea label="Ret Gerekçesi *" rows={2} value={transModal.reject_reason || ""} onChange={e => setTransModal({ ...transModal, reject_reason: e.target.value })} data-testid="cc-trans-reject-reason"/>}
          {(transModal.action === "effectiveness" || transModal.action === "close") && <Textarea label="Etkinlik Kontrolü Sonucu" rows={2} value={transModal.effectiveness_result || ""} onChange={e => setTransModal({ ...transModal, effectiveness_result: e.target.value })} data-testid="cc-trans-eff"/>}
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setTransModal(null)}>Vazgeç</Btn><Btn onClick={doTransition} data-testid="cc-trans-confirm">{transModal?.label}</Btn></div>
      </Modal>
    </div>
  );
}

function SnapshotChip({ label, value }) {
  return <div className="bg-slate-50 border border-slate-200 rounded p-1.5"><div className="text-[9px] text-slate-500 uppercase">{label}</div><div className="font-mono">{value}</div></div>;
}


/* ============= 2) INTERNAL AUDIT ============= */
const AUD_TYPES = [
  { k: "internal", l: "İç Denetim" }, { k: "external", l: "Dış Denetim" },
  { k: "vendor", l: "Tedarikçi" }, { k: "regulatory", l: "Regülatör" },
];
const FND_CLASSES = [
  { k: "observation", l: "Gözlem" }, { k: "minor", l: "Minör" },
  { k: "major", l: "Majör" }, { k: "critical", l: "Kritik" },
];

export function InternalAuditTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ status: "", type: "", q: "" });
  const [modal, setModal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [fndModal, setFndModal] = useState(null);
  const [warn, setWarn] = useState("");

  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setRows((await api.get("/gmp/audits", params)) || []);
  };
  useEffect(() => { load(); }, [f.status, f.type]);

  const openCreate = () => setModal({ mode: "create", doc: { type: "internal", scope: "", department: "", planned_date: today(), lead_auditor: "", auditors: [] } });
  const save = async () => {
    if (!modal.doc.scope?.trim()) return alert("Kapsam zorunlu");
    try {
      if (modal.mode === "create") await api.post("/gmp/audits", modal.doc);
      else await api.put(`/gmp/audits/${modal.doc.id}`, modal.doc);
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const start = async (r) => { try { await api.post(`/gmp/audits/${r.id}/start`); load(); const d = await api.get(`/gmp/audits/${r.id}`); setDetail(d); } catch (e) { alert(e.response?.data?.detail); } };
  const complete = async (r) => { const s = prompt("Denetim özeti (opsiyonel)"); try { await api.post(`/gmp/audits/${r.id}/complete`, { summary: s || undefined }); load(); const d = await api.get(`/gmp/audits/${r.id}`); setDetail(d); } catch (e) { alert(e.response?.data?.detail); } };
  const closeAud = async (r) => {
    if (!window.confirm("Denetim kapatılsın mı?")) return;
    try {
      const res = await api.post(`/gmp/audits/${r.id}/close`, {});
      if (res?.warning?.open_major_critical_findings) {
        setWarn(`⚠ Kapatıldı ancak ${res.warning.open_major_critical_findings} açık MAJÖR/KRİTİK bulgu var. CAPA açmayı düşünün.`);
      }
      load(); const d = await api.get(`/gmp/audits/${r.id}`); setDetail(d);
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const del = async (r) => { if (window.confirm(`${r.number} arşivlensin mi?`)) { try { await api.del(`/gmp/audits/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };

  const openFinding = () => setFndModal({ audit_id: detail.id, requirement: "", description: "", classification: "observation", target_date: "", action: "", responsible_user: "" });
  const saveFinding = async () => {
    if (!fndModal.description?.trim()) return alert("Açıklama zorunlu");
    try { await api.post("/gmp/audit-findings", fndModal); setFndModal(null); const d = await api.get(`/gmp/audits/${detail.id}`); setDetail(d); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const closeFinding = async (fnd) => {
    const res = prompt("Çözüm / Alınan aksiyon", fnd.action || "");
    if (!res) return;
    try { await api.post(`/gmp/audit-findings/${fnd.id}/close`, { resolution: res }); const d = await api.get(`/gmp/audits/${detail.id}`); setDetail(d); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const fndCapa = async (fnd) => {
    try { await api.post(`/gmp/audit-findings/${fnd.id}/capa`, {}); alert("CAPA açıldı"); const d = await api.get(`/gmp/audits/${detail.id}`); setDetail(d); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const delFnd = async (fnd) => { if (window.confirm(`${fnd.number} silinsin mi?`)) { try { await api.del(`/gmp/audit-findings/${fnd.id}`); const d = await api.get(`/gmp/audits/${detail.id}`); setDetail(d); } catch (e) { alert(e.response?.data?.detail); } } };

  return (
    <div>
      {warn && <div className="bg-amber-50 border border-amber-300 rounded p-2 mb-3 text-xs text-amber-900 flex items-center justify-between" data-testid="aud-warn">
        <span>{warn}</span><button onClick={() => setWarn("")} className="text-slate-500">✕</button>
      </div>}
      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border rounded-lg p-3">
        <div className="min-w-[200px] flex-1"><Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} data-testid="aud-search"/></div>
        <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="aud-filter-status">
          <option value="">Tümü</option><option value="planned">Planlanmış</option>
          <option value="in_progress">Devam Ediyor</option><option value="completed">Tamamlandı</option>
          <option value="closed">Kapalı</option><option value="cancelled">İptal</option>
        </Select>
        <Select label="Tip" value={f.type} onChange={e => setF({ ...f, type: e.target.value })} data-testid="aud-filter-type">
          <option value="">Tümü</option>{AUD_TYPES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
        </Select>
        <div className="flex-1"/>
        {isAdmin && <Btn onClick={openCreate} data-testid="aud-new"><Plus size={14}/> Yeni Denetim</Btn>}
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]" data-testid="aud-table">
          <thead className="bg-slate-100 text-slate-700 text-xs"><tr>
            <th className="text-left px-3 py-2">No</th><th className="text-left px-3 py-2">Kapsam</th>
            <th className="text-left px-3 py-2">Tip</th><th className="text-left px-3 py-2">Departman</th>
            <th className="text-left px-3 py-2">Baş Denetçi</th><th className="text-left px-3 py-2">Durum</th>
            <th className="text-left px-3 py-2">Plan</th><th className="text-left px-3 py-2 w-60">İşlem</th>
          </tr></thead>
          <tbody>
            {rows.filter(r => !f.q || (r.number + r.scope).toLowerCase().includes(f.q.toLowerCase())).map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`aud-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                <td className="px-3 py-2 font-semibold">{r.scope}</td>
                <td className="px-3 py-2 text-xs">{AUD_TYPES.find(x => x.k === r.type)?.l || r.type}</td>
                <td className="px-3 py-2 text-xs">{r.department || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.lead_auditor || "—"}</td>
                <td className="px-3 py-2"><Badge level={STATUS_LEVEL[r.status]}>{r.status}</Badge></td>
                <td className="px-3 py-2 text-xs">{r.planned_date || "—"}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <Btn size="sm" variant="ghost" onClick={async () => setDetail(await api.get(`/gmp/audits/${r.id}`))} data-testid={`aud-view-${r.id}`}>Detay</Btn>
                    {r.status === "planned" && <Btn size="sm" variant="primary" onClick={() => start(r)} data-testid={`aud-start-${r.id}`}>Başlat</Btn>}
                    {r.status === "in_progress" && <Btn size="sm" variant="secondary" onClick={() => complete(r)} data-testid={`aud-complete-${r.id}`}>Tamamla</Btn>}
                    {(r.status === "completed" || r.status === "in_progress") && <Btn size="sm" onClick={() => closeAud(r)} data-testid={`aud-close-${r.id}`}>Kapat</Btn>}
                    <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`aud-delete-${r.id}`}>Sil</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Denetim kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.mode === "create" ? "Yeni Denetim" : "Denetim Düzenle"} size="lg">
        {modal && (<div className="grid grid-cols-2 gap-3">
          <Select label="Tip" value={modal.doc.type} onChange={e => setModal({ ...modal, doc: { ...modal.doc, type: e.target.value } })} data-testid="aud-form-type">
            {AUD_TYPES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
          </Select>
          <Input label="Planlanan Tarih" type="date" value={modal.doc.planned_date || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, planned_date: e.target.value } })} data-testid="aud-form-planned"/>
          <div className="col-span-2"><Input label="Kapsam *" value={modal.doc.scope} onChange={e => setModal({ ...modal, doc: { ...modal.doc, scope: e.target.value } })} data-testid="aud-form-scope"/></div>
          <Input label="Departman" value={modal.doc.department || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, department: e.target.value } })} data-testid="aud-form-dept"/>
          <Input label="Alan" value={modal.doc.area || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, area: e.target.value } })} data-testid="aud-form-area"/>
          <Input label="Baş Denetçi" value={modal.doc.lead_auditor || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, lead_auditor: e.target.value } })} data-testid="aud-form-lead"/>
          <Input label="Diğer Denetçiler (virgülle)" value={(modal.doc.auditors || []).join(", ")} onChange={e => setModal({ ...modal, doc: { ...modal.doc, auditors: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } })} data-testid="aud-form-others"/>
          <div className="col-span-2"><Textarea label="Özet / Notlar" rows={3} value={modal.doc.summary || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, summary: e.target.value } })} data-testid="aud-form-summary"/></div>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn><Btn onClick={save} data-testid="aud-form-save">Kaydet</Btn></div>
      </Modal>

      {/* DETAIL + FINDINGS */}
      <Modal open={!!detail} onClose={() => setDetail(null)} title={`Denetim ${detail?.number || ""}`} size="lg">
        {detail && (<div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div><b>Durum:</b> <Badge level={STATUS_LEVEL[detail.status]}>{detail.status}</Badge></div>
            <div><b>Tip:</b> {AUD_TYPES.find(x => x.k === detail.type)?.l}</div>
            <div><b>Baş Denetçi:</b> {detail.lead_auditor || "—"}</div>
            <div><b>Kapsam:</b> {detail.scope}</div>
            <div><b>Departman:</b> {detail.department || "—"}</div>
            <div><b>Plan / Fiili:</b> {detail.planned_date || "—"} / {detail.actual_date || "—"}</div>
          </div>
          {detail.summary && <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Özet</div><div className="bg-slate-50 border rounded p-2 whitespace-pre-wrap">{detail.summary}</div></div>}
          <div className="border-t pt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase font-semibold text-slate-600">Bulgular ({(detail.findings || []).length})</div>
              {isAdmin && detail.status !== "closed" && detail.status !== "cancelled" && <Btn size="sm" onClick={openFinding} data-testid="aud-add-finding"><Plus size={12}/> Yeni Bulgu</Btn>}
            </div>
            <div className="bg-white border rounded overflow-x-auto">
              <table className="w-full text-xs" data-testid="aud-findings-table">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  <th className="text-left px-2 py-1">No</th><th className="text-left px-2 py-1">Gereksinim</th>
                  <th className="text-left px-2 py-1">Sınıf</th><th className="text-left px-2 py-1">Sorumlu</th>
                  <th className="text-left px-2 py-1">Hedef</th><th className="text-left px-2 py-1">Durum</th>
                  <th className="text-left px-2 py-1">İşlem</th>
                </tr></thead>
                <tbody>
                  {(detail.findings || []).map(fnd => (
                    <tr key={fnd.id} className="border-t" data-testid={`fnd-row-${fnd.id}`}>
                      <td className="px-2 py-1 font-mono">{fnd.number}</td>
                      <td className="px-2 py-1">{fnd.requirement || "—"}<div className="text-slate-500 text-[10px]">{(fnd.description || "").slice(0, 100)}</div></td>
                      <td className="px-2 py-1"><Badge level={CLASS_LEVEL[fnd.classification]}>{fnd.classification}</Badge></td>
                      <td className="px-2 py-1">{fnd.responsible_user || "—"}</td>
                      <td className="px-2 py-1">{fnd.target_date || "—"}</td>
                      <td className="px-2 py-1"><Badge level={STATUS_LEVEL[fnd.status]}>{fnd.status}</Badge></td>
                      <td className="px-2 py-1">
                        <div className="flex gap-1">
                          {fnd.status !== "closed" && <Btn size="sm" onClick={() => closeFinding(fnd)} data-testid={`fnd-close-${fnd.id}`}>Kapat</Btn>}
                          {fnd.status !== "closed" && <Btn size="sm" variant="secondary" onClick={() => fndCapa(fnd)} data-testid={`fnd-capa-${fnd.id}`}>CAPA</Btn>}
                          <Btn size="sm" variant="danger" onClick={() => delFnd(fnd)} data-testid={`fnd-delete-${fnd.id}`}>Sil</Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {(detail.findings || []).length === 0 && <tr><td colSpan={7} className="text-center py-4 text-slate-400">Bulgu yok.</td></tr>}
                </tbody>
              </table>
            </div>
            <GmpEvidencePanel module="internal_audit" recordId={detail.id}/>
          </div>
        </div>)}
      </Modal>

      <Modal open={!!fndModal} onClose={() => setFndModal(null)} title="Yeni Bulgu" size="md">
        {fndModal && (<div className="space-y-3">
          <Input label="Gereksinim / Konu" value={fndModal.requirement} onChange={e => setFndModal({ ...fndModal, requirement: e.target.value })} data-testid="fnd-form-req"/>
          <Textarea label="Açıklama *" rows={3} value={fndModal.description} onChange={e => setFndModal({ ...fndModal, description: e.target.value })} data-testid="fnd-form-desc"/>
          <Select label="Sınıflandırma" value={fndModal.classification} onChange={e => setFndModal({ ...fndModal, classification: e.target.value })} data-testid="fnd-form-class">
            {FND_CLASSES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
          </Select>
          <Input label="Sorumlu" value={fndModal.responsible_user} onChange={e => setFndModal({ ...fndModal, responsible_user: e.target.value })} data-testid="fnd-form-resp"/>
          <Input label="Hedef Tarih" type="date" value={fndModal.target_date} onChange={e => setFndModal({ ...fndModal, target_date: e.target.value })} data-testid="fnd-form-target"/>
          <Textarea label="Aksiyon" rows={2} value={fndModal.action} onChange={e => setFndModal({ ...fndModal, action: e.target.value })} data-testid="fnd-form-action"/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setFndModal(null)}>Vazgeç</Btn><Btn onClick={saveFinding} data-testid="fnd-form-save">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}


/* ============= 3) RISK MANAGEMENT ============= */
const RISK_SOURCES = [
  { k: "process", l: "Süreç" }, { k: "raw_material", l: "Hammadde" }, { k: "product", l: "Ürün" },
  { k: "formula", l: "Formül" }, { k: "equipment", l: "Ekipman" }, { k: "supplier", l: "Tedarikçi" },
  { k: "gmp_area", l: "GMP Alan" }, { k: "change_control", l: "Değişiklik Kontrolü" },
  { k: "deviation", l: "Sapma" }, { k: "complaint", l: "Şikayet" }, { k: "oos", l: "OOS" }, { k: "other", l: "Diğer" },
];
const ACCEPT_DECISIONS = [
  { k: "accept", l: "Kabul Et" }, { k: "mitigate", l: "Azalt" },
  { k: "transfer", l: "Transfer Et" }, { k: "avoid", l: "Kaçın" },
];

export function RiskTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [thresholds, setThresholds] = useState({ low_max: 40, medium_max: 100, high_max: 200, scale_min: 1, scale_max: 5 });
  const [f, setF] = useState({ status: "", source_type: "", classification: "", q: "" });
  const [modal, setModal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [closeModal, setCloseModal] = useState(null);

  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setRows((await api.get("/gmp/risks", params)) || []);
  };
  useEffect(() => { load(); }, [f.status, f.source_type, f.classification]);
  useEffect(() => { api.get("/gmp/qms-config/risk-thresholds").then(setThresholds).catch(() => {}); }, []);

  const openCreate = () => setModal({ mode: "create", doc: {
    title: "", description: "", source_type: "process",
    severity: 3, probability: 3, detectability: 3,
    existing_controls: "", mitigation_action: "",
  } });
  const save = async () => {
    if (!modal.doc.title?.trim() || !modal.doc.description?.trim()) return alert("Başlık ve açıklama zorunlu");
    try {
      if (modal.mode === "create") await api.post("/gmp/risks", modal.doc);
      else await api.put(`/gmp/risks/${modal.doc.id}`, modal.doc);
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const accept = async (r) => {
    const decision = prompt("Kabul kararı: accept / mitigate / transfer / avoid", "mitigate");
    if (!decision) return;
    const notes = prompt("Not (opsiyonel)") || "";
    try { await api.post(`/gmp/risks/${r.id}/accept`, { decision, notes }); load(); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const doClose = async () => {
    if (!closeModal.closure_notes?.trim()) return alert("Kapanış notu zorunlu");
    try { await api.post(`/gmp/risks/${closeModal.doc.id}/close`, closeModal); setCloseModal(null); load(); }
    catch (e) { alert(e.response?.data?.detail); }
  };
  const del = async (r) => { if (window.confirm(`${r.number} silinsin mi?`)) { try { await api.del(`/gmp/risks/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };
  const openCapa = async (r) => {
    const t = prompt("CAPA başlığı", `CAPA — Risk ${r.number}: ${r.title}`);
    if (!t) return;
    try { await api.post(`/gmp/risks/${r.id}/capa`, { title: t, priority: r.current_classification === "critical" ? "critical" : "high" }); alert("CAPA açıldı"); load(); }
    catch (e) { alert(e.response?.data?.detail); }
  };

  const rpnPreview = useMemo(() => {
    if (!modal) return 0;
    return (modal.doc.severity || 0) * (modal.doc.probability || 0) * (modal.doc.detectability || 0);
  }, [modal]);

  return (
    <div>
      <div className="bg-sky-50/60 border border-sky-200 rounded-lg p-3 mb-3 text-xs text-sky-900">
        <b>ℹ Risk Skorlama:</b> RPN = Şiddet × Olasılık × Tespit. Skorlar {thresholds.scale_min}..{thresholds.scale_max}.
        Sınıflar: LOW ≤ {thresholds.low_max}, MED ≤ {thresholds.medium_max}, HIGH ≤ {thresholds.high_max}, üstü CRITICAL.
        <b> Kabul kararını kullanıcı verir</b> — sistem regülatör olarak zorlama yapmaz.
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border rounded-lg p-3">
        <div className="min-w-[200px] flex-1"><Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} data-testid="risk-search"/></div>
        <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })} data-testid="risk-filter-status">
          <option value="">Tümü</option><option value="open">Açık</option><option value="mitigating">Azaltılıyor</option>
          <option value="monitoring">İzlemede</option><option value="closed">Kapalı</option>
        </Select>
        <Select label="Kaynak" value={f.source_type} onChange={e => setF({ ...f, source_type: e.target.value })} data-testid="risk-filter-src">
          <option value="">Tümü</option>{RISK_SOURCES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
        </Select>
        <Select label="Sınıf" value={f.classification} onChange={e => setF({ ...f, classification: e.target.value })} data-testid="risk-filter-class">
          <option value="">Tümü</option><option value="low">Düşük</option><option value="medium">Orta</option><option value="high">Yüksek</option><option value="critical">Kritik</option>
        </Select>
        <div className="flex-1"/>
        {isAdmin && <Btn onClick={openCreate} data-testid="risk-new"><Plus size={14}/> Yeni Risk</Btn>}
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]" data-testid="risk-table">
          <thead className="bg-slate-100 text-slate-700 text-xs"><tr>
            <th className="text-left px-3 py-2">No</th><th className="text-left px-3 py-2">Başlık</th>
            <th className="text-left px-3 py-2">Kaynak</th><th className="text-left px-3 py-2">Initial RPN</th>
            <th className="text-left px-3 py-2">Residual RPN</th><th className="text-left px-3 py-2">Karar</th>
            <th className="text-left px-3 py-2">Durum</th><th className="text-left px-3 py-2 w-56">İşlem</th>
          </tr></thead>
          <tbody>
            {rows.filter(r => !f.q || (r.number + r.title).toLowerCase().includes(f.q.toLowerCase())).map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`risk-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                <td className="px-3 py-2 font-semibold">{r.title}</td>
                <td className="px-3 py-2 text-xs">{RISK_SOURCES.find(x => x.k === r.source_type)?.l || r.source_type}</td>
                <td className="px-3 py-2 text-xs">
                  <span className="font-mono">{r.initial_rpn}</span> <Badge level={RPN_LEVEL[r.current_classification]}>{r.current_classification}</Badge>
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.residual_rpn ? <><span className="font-mono">{r.residual_rpn}</span> <Badge level={RPN_LEVEL[r.residual_classification]}>{r.residual_classification}</Badge></> : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs">{ACCEPT_DECISIONS.find(x => x.k === r.acceptance_decision)?.l || "—"}</td>
                <td className="px-3 py-2"><Badge level={STATUS_LEVEL[r.status]}>{r.status}</Badge></td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <Btn size="sm" variant="ghost" onClick={() => setDetail(r)} data-testid={`risk-view-${r.id}`}>Detay</Btn>
                    {r.status !== "closed" && !r.acceptance_decision && <Btn size="sm" variant="secondary" onClick={() => accept(r)} data-testid={`risk-accept-${r.id}`}>Karar Ver</Btn>}
                    {r.status !== "closed" && <Btn size="sm" onClick={() => setCloseModal({ doc: r, closure_notes: "" })} data-testid={`risk-close-${r.id}`}>Kapat</Btn>}
                    {r.status !== "closed" && <Btn size="sm" variant="secondary" onClick={() => openCapa(r)} data-testid={`risk-capa-${r.id}`}>CAPA</Btn>}
                    <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`risk-delete-${r.id}`}>Sil</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Risk kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title="Yeni Risk" size="lg">
        {modal && (<div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Başlık *" value={modal.doc.title} onChange={e => setModal({ ...modal, doc: { ...modal.doc, title: e.target.value } })} data-testid="risk-form-title"/></div>
          <div className="col-span-2"><Textarea label="Açıklama *" rows={3} value={modal.doc.description} onChange={e => setModal({ ...modal, doc: { ...modal.doc, description: e.target.value } })} data-testid="risk-form-desc"/></div>
          <Select label="Kaynak Tipi" value={modal.doc.source_type} onChange={e => setModal({ ...modal, doc: { ...modal.doc, source_type: e.target.value } })} data-testid="risk-form-src">
            {RISK_SOURCES.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
          </Select>
          <Input label="Sorumlu" value={modal.doc.responsible_user || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, responsible_user: e.target.value } })}/>
          <Input label={`Şiddet (${thresholds.scale_min}-${thresholds.scale_max})`} type="number" min={thresholds.scale_min} max={thresholds.scale_max} value={modal.doc.severity} onChange={e => setModal({ ...modal, doc: { ...modal.doc, severity: parseInt(e.target.value) || 0 } })} data-testid="risk-form-sev"/>
          <Input label="Olasılık" type="number" min={thresholds.scale_min} max={thresholds.scale_max} value={modal.doc.probability} onChange={e => setModal({ ...modal, doc: { ...modal.doc, probability: parseInt(e.target.value) || 0 } })} data-testid="risk-form-prob"/>
          <Input label="Tespit" type="number" min={thresholds.scale_min} max={thresholds.scale_max} value={modal.doc.detectability} onChange={e => setModal({ ...modal, doc: { ...modal.doc, detectability: parseInt(e.target.value) || 0 } })} data-testid="risk-form-det"/>
          <div className="col-span-1 flex items-end"><div className="w-full bg-sky-50 border border-sky-200 rounded p-2 text-center text-sm"><b>Initial RPN:</b> <span className="font-mono text-lg" data-testid="risk-form-rpn">{rpnPreview}</span></div></div>
          <div className="col-span-2"><Textarea label="Mevcut Kontroller" rows={2} value={modal.doc.existing_controls || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, existing_controls: e.target.value } })} data-testid="risk-form-ctrl"/></div>
          <div className="col-span-2"><Textarea label="Azaltma Aksiyonu" rows={2} value={modal.doc.mitigation_action || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, mitigation_action: e.target.value } })} data-testid="risk-form-mit"/></div>
          <Input label="Hedef Tarih" type="date" value={modal.doc.target_date || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, target_date: e.target.value } })}/>
          <Input label="Alan" value={modal.doc.area || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, area: e.target.value } })}/>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn><Btn onClick={save} data-testid="risk-form-save">Kaydet</Btn></div>
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title={`Risk ${detail?.number || ""}`} size="lg">
        {detail && (<div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div><b>Başlık:</b> {detail.title}</div>
            <div><b>Durum:</b> <Badge level={STATUS_LEVEL[detail.status]}>{detail.status}</Badge></div>
            <div><b>Initial:</b> S={detail.severity} × P={detail.probability} × D={detail.detectability} = <span className="font-mono">{detail.initial_rpn}</span> <Badge level={RPN_LEVEL[detail.initial_classification]}>{detail.initial_classification}</Badge></div>
            <div><b>Residual:</b> {detail.residual_rpn ? <><span className="font-mono">{detail.residual_rpn}</span> <Badge level={RPN_LEVEL[detail.residual_classification]}>{detail.residual_classification}</Badge></> : "—"}</div>
            <div><b>Karar:</b> {ACCEPT_DECISIONS.find(x => x.k === detail.acceptance_decision)?.l || "—"}</div>
            <div><b>Kaynak:</b> {RISK_SOURCES.find(x => x.k === detail.source_type)?.l}</div>
          </div>
          <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Açıklama</div><div className="bg-slate-50 border rounded p-2 whitespace-pre-wrap">{detail.description}</div></div>
          {detail.existing_controls && <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Mevcut Kontroller</div><div className="bg-slate-50 border rounded p-2 whitespace-pre-wrap">{detail.existing_controls}</div></div>}
          {detail.mitigation_action && <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Azaltma Aksiyonu</div><div className="bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap">{detail.mitigation_action}</div></div>}
          {detail.closure_notes && <div><div className="text-[10px] uppercase text-slate-500 mb-0.5">Kapanış Notu</div><div className="bg-emerald-50 border border-emerald-200 rounded p-2 whitespace-pre-wrap">{detail.closure_notes}</div></div>}
          <GmpEvidencePanel module="risk" recordId={detail.id}/>
        </div>)}
      </Modal>

      <Modal open={!!closeModal} onClose={() => setCloseModal(null)} title={`Risk Kapat: ${closeModal?.doc?.number || ""}`} size="md">
        {closeModal && (<div className="space-y-3">
          <Textarea label="Kapanış Notu *" rows={3} value={closeModal.closure_notes} onChange={e => setCloseModal({ ...closeModal, closure_notes: e.target.value })} data-testid="risk-close-notes"/>
          <div className="grid grid-cols-3 gap-2">
            <Input label="Residual S" type="number" min={thresholds.scale_min} max={thresholds.scale_max} value={closeModal.residual_severity || ""} onChange={e => setCloseModal({ ...closeModal, residual_severity: parseInt(e.target.value) || null })} data-testid="risk-close-rs"/>
            <Input label="Residual P" type="number" min={thresholds.scale_min} max={thresholds.scale_max} value={closeModal.residual_probability || ""} onChange={e => setCloseModal({ ...closeModal, residual_probability: parseInt(e.target.value) || null })} data-testid="risk-close-rp"/>
            <Input label="Residual D" type="number" min={thresholds.scale_min} max={thresholds.scale_max} value={closeModal.residual_detectability || ""} onChange={e => setCloseModal({ ...closeModal, residual_detectability: parseInt(e.target.value) || null })} data-testid="risk-close-rd"/>
          </div>
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setCloseModal(null)}>Vazgeç</Btn><Btn onClick={doClose} data-testid="risk-close-confirm">Kapat</Btn></div>
      </Modal>
    </div>
  );
}


/* ============= 4) WASTE ============= */
const WASTE_CATS = [
  { k: "raw_material", l: "Hammadde" }, { k: "packaging", l: "Ambalaj" },
  { k: "finished_product", l: "Bitmiş Ürün" }, { k: "intermediate", l: "Ara Ürün" },
  { k: "sample", l: "Numune" }, { k: "cleaning_waste", l: "Temizlik" },
  { k: "hazardous", l: "Tehlikeli" }, { k: "solvent", l: "Solvent" },
  { k: "electronic", l: "Elektronik" }, { k: "office", l: "Ofis" }, { k: "other", l: "Diğer" },
];

export function WasteTab() {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ waste_category: "", q: "" });
  const [modal, setModal] = useState(null);
  const [refs, setRefs] = useState({ materials: [], products: [], lots: [] });

  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    setRows((await api.get("/gmp/waste-records", params)) || []);
  };
  useEffect(() => { load(); }, [f.waste_category]);
  useEffect(() => {
    Promise.all([
      api.get("/materials").catch(() => []),
      api.get("/products").catch(() => []),
      api.get("/inventory/lots").catch(() => []),
    ]).then(([m, p, l]) => setRefs({ materials: m || [], products: p || [], lots: l || [] }));
  }, []);

  const openCreate = () => setModal({ mode: "create", doc: { date: today(), waste_category: "other", quantity: 0, unit: "kg" } });
  const save = async () => {
    try {
      if (modal.mode === "create") await api.post("/gmp/waste-records", modal.doc);
      else await api.put(`/gmp/waste-records/${modal.doc.id}`, modal.doc);
      setModal(null); load();
    } catch (e) { alert(e.response?.data?.detail); }
  };
  const del = async (r) => { if (window.confirm(`${r.number} silinsin mi?`)) { try { await api.del(`/gmp/waste-records/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };

  return (
    <div>
      <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-2 mb-3 text-xs text-emerald-900">
        <b>ℹ Atık Kaydı:</b> Bu kayıt <b>stok değişimi yapmaz</b> — sadece belgelemedir. Stok düşümü için mevcut Envanter modülü kullanılmalıdır.
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-3 bg-slate-50 border rounded-lg p-3">
        <div className="min-w-[200px] flex-1"><Input label="Arama" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} data-testid="waste-search"/></div>
        <Select label="Kategori" value={f.waste_category} onChange={e => setF({ ...f, waste_category: e.target.value })} data-testid="waste-filter-cat">
          <option value="">Tümü</option>{WASTE_CATS.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
        </Select>
        <div className="flex-1"/>
        <Btn onClick={openCreate} data-testid="waste-new"><Plus size={14}/> Yeni Atık Kaydı</Btn>
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="waste-table">
          <thead className="bg-slate-100 text-slate-700 text-xs"><tr>
            <th className="text-left px-3 py-2">No</th><th className="text-left px-3 py-2">Tarih</th>
            <th className="text-left px-3 py-2">Kategori</th><th className="text-left px-3 py-2">Malzeme/Ürün</th>
            <th className="text-left px-3 py-2">Miktar</th><th className="text-left px-3 py-2">İmha Yöntemi</th>
            <th className="text-left px-3 py-2">Firma</th><th className="text-left px-3 py-2 w-32">İşlem</th>
          </tr></thead>
          <tbody>
            {rows.filter(r => !f.q || (r.number + (r.reason || "") + (r.disposal_company || "")).toLowerCase().includes(f.q.toLowerCase())).map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`waste-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.number}</td>
                <td className="px-3 py-2 text-xs">{r.date}</td>
                <td className="px-3 py-2 text-xs">{WASTE_CATS.find(x => x.k === r.waste_category)?.l}</td>
                <td className="px-3 py-2 text-xs">
                  {r.material_snapshot?.name || r.product_snapshot?.name || "—"}
                  {(r.lot_code_snapshot || r.lot_code) && <div className="text-[10px] font-mono text-slate-500">Lot: {r.lot_code_snapshot || r.lot_code}</div>}
                </td>
                <td className="px-3 py-2 text-xs font-mono">{r.quantity} {r.unit}</td>
                <td className="px-3 py-2 text-xs">{r.disposal_method || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.disposal_company || "—"}</td>
                <td className="px-3 py-2">
                  <Btn size="sm" variant="ghost" onClick={() => setModal({ mode: "edit", doc: { ...r } })} data-testid={`waste-edit-${r.id}`}>Düzenle</Btn>
                  <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`waste-delete-${r.id}`}>Sil</Btn>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Atık kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal?.mode === "create" ? "Yeni Atık Kaydı" : "Atık Düzenle"} size="lg">
        {modal && (<div className="grid grid-cols-2 gap-3">
          <Input label="Tarih *" type="date" value={modal.doc.date} onChange={e => setModal({ ...modal, doc: { ...modal.doc, date: e.target.value } })} data-testid="waste-form-date"/>
          <Select label="Kategori" value={modal.doc.waste_category} onChange={e => setModal({ ...modal, doc: { ...modal.doc, waste_category: e.target.value } })} data-testid="waste-form-cat">
            {WASTE_CATS.map(x => <option key={x.k} value={x.k}>{x.l}</option>)}
          </Select>
          <Input label="Kaynak Departman" value={modal.doc.source_department || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, source_department: e.target.value } })}/>
          <Input label="Miktar" type="number" step="0.01" value={modal.doc.quantity} onChange={e => setModal({ ...modal, doc: { ...modal.doc, quantity: parseFloat(e.target.value) || 0 } })} data-testid="waste-form-qty"/>
          <Input label="Birim" value={modal.doc.unit} onChange={e => setModal({ ...modal, doc: { ...modal.doc, unit: e.target.value } })} data-testid="waste-form-unit"/>
          <Select label="Hammadde (ops.)" value={modal.doc.material_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, material_id: e.target.value } })} data-testid="waste-form-mat">
            <option value="">— Seç —</option>{refs.materials.slice(0, 500).map(m => <option key={m.id} value={m.id}>{m.code} — {m.name}</option>)}
          </Select>
          <Select label="Ürün (ops.)" value={modal.doc.product_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, product_id: e.target.value } })}>
            <option value="">— Seç —</option>{refs.products.slice(0, 500).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Lot (ops.)" value={modal.doc.lot_id || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, lot_id: e.target.value } })}>
            <option value="">— Seç —</option>{refs.lots.slice(0, 500).map(l => <option key={l.id} value={l.id}>{l.lot_code}</option>)}
          </Select>
          <Input label="Manuel Lot Kodu" value={modal.doc.lot_code || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, lot_code: e.target.value } })}/>
          <div className="col-span-2"><Textarea label="Neden" rows={2} value={modal.doc.reason || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, reason: e.target.value } })}/></div>
          <Input label="İmha Yöntemi" value={modal.doc.disposal_method || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, disposal_method: e.target.value } })}/>
          <Input label="İmha Firması" value={modal.doc.disposal_company || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, disposal_company: e.target.value } })}/>
          <Input label="Belge No" value={modal.doc.document_no || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, document_no: e.target.value } })}/>
          <Input label="Sorumlu" value={modal.doc.responsible_user || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, responsible_user: e.target.value } })}/>
          <Input label="Kontrol Eden" value={modal.doc.checked_by || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, checked_by: e.target.value } })}/>
          <div className="col-span-2"><Textarea label="Notlar" rows={2} value={modal.doc.notes || ""} onChange={e => setModal({ ...modal, doc: { ...modal.doc, notes: e.target.value } })}/></div>
          {modal.mode === "edit" && modal.doc.id && <div className="col-span-2"><GmpEvidencePanel module="waste" recordId={modal.doc.id}/></div>}
        </div>)}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setModal(null)}>Vazgeç</Btn><Btn onClick={save} data-testid="waste-form-save">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}


/* ============= 5) RECALL SUMMARY (read-only, mevcut modül authoritative) ============= */
export function RecallSummaryTab() {
  const [data, setData] = useState({ active_count: 0, mock_count: 0, total_count: 0, recent: [] });
  useEffect(() => { api.get("/gmp/recall-summary").then(setData).catch(() => {}); }, []);
  return (
    <div>
      <div className="bg-rose-50/60 border border-rose-200 rounded-lg p-3 mb-3 text-xs text-rose-900">
        <b>ℹ Geri Çağırma (Recall):</b> Yönetim mevcut Geri Çağırma modülünde yapılır. Bu panel yalnızca özet gösterir.
        Yeni recall açmak / mock çalıştırmak / lot arayıp geri çağırma başlatmak için modüle geçin.
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="p-4 border-l-4 border-rose-500" data-testid="recall-active-card">
          <div className="text-xs text-slate-500 uppercase">Aktif Recall</div>
          <div className="text-3xl font-bold text-rose-700 font-mono">{data.active_count}</div>
        </Card>
        <Card className="p-4 border-l-4 border-amber-500" data-testid="recall-mock-card">
          <div className="text-xs text-slate-500 uppercase">Mock Recall</div>
          <div className="text-3xl font-bold text-amber-700 font-mono">{data.mock_count}</div>
        </Card>
        <Card className="p-4 border-l-4 border-slate-400" data-testid="recall-total-card">
          <div className="text-xs text-slate-500 uppercase">Toplam Kayıt</div>
          <div className="text-3xl font-bold text-slate-700 font-mono">{data.total_count}</div>
        </Card>
      </div>
      <div className="flex justify-end mb-2">
        <a href="/recalls" data-testid="recall-goto"><Btn variant="primary"><ExternalLink size={12}/> Geri Çağırma Modülüne Git</Btn></a>
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="recall-recent-table">
          <thead className="bg-slate-100 text-slate-700 text-xs"><tr>
            <th className="text-left px-3 py-2">No</th><th className="text-left px-3 py-2">Mod</th>
            <th className="text-left px-3 py-2">Durum</th><th className="text-left px-3 py-2">Neden</th>
            <th className="text-left px-3 py-2">Lot Sayısı</th><th className="text-left px-3 py-2">Tarih</th>
          </tr></thead>
          <tbody>
            {(data.recent || []).map(r => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`recall-recent-${r.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.number || r.id?.slice(0, 8)}</td>
                <td className="px-3 py-2 text-xs">{r.mode === "mock" ? <Badge level="attention">Mock</Badge> : <Badge level="missing">Live</Badge>}</td>
                <td className="px-3 py-2 text-xs">{r.status || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.reason || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono">{(r.lot_ids || []).length}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(r.created_at)}</td>
              </tr>
            ))}
            {(data.recent || []).length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Recall kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
